import { theme } from "@common/theme/theme"
import { IS_WEB } from "@common/utils/mobileUtils"

export const EVENT_TYPES = {
    HAND_RAISED: "hand_raised",
    OUT_OF_APP: "out_of_app",
    SILENCE: "silence",
    EXERCISE_DONE: "exercise_done",
    SAM_ONGOING: "sam_ongoing",
    AUTO_ACTIVITY: "auto_activity",
}

// Short labels for the student's auto-evaluation, surfaced in the tutor card so
// a single auto-mode event carries both pieces of info: what the student
// declared AND which activity they picked (reachable via the "Voir l'énoncé"
// CTA). Keyed by the selectedEval set in the auto-eval overlay.
export const AUTO_EVAL_LABELS = {
    got_it: "tout compris",
    mixed: "pas tout compris",
    lost: "bloqué",
}

// What the tutor's card reads when a student picks their own next activity.
// "replay" isn't a self-evaluation — it's the "Refaire la même activité"
// shortcut, which skips the eval entirely — so it gets its own wording rather
// than an AUTO_EVAL_LABELS entry that would read "s'est auto-évalué « … »".
export const autoActivityVerb = (evaluation) => {
    if (evaluation === "replay") return "a choisi de refaire la même activité"
    const evalLabel = AUTO_EVAL_LABELS[evaluation]
    return evalLabel
        ? `s'est auto-évalué « ${evalLabel} » et a choisi sa prochaine activité`
        : "a choisi seul sa prochaine activité"
}

export const ESCALATION_DELAY_MS = 4 * 60 * 1000

// Event types that can route through Sam before escalating to the tutor.
export const SAM_ELIGIBLE = new Set([
    EVENT_TYPES.HAND_RAISED,
    EVENT_TYPES.SILENCE,
])

export const EVENT_CATALOG = {
    [EVENT_TYPES.HAND_RAISED]: {
        emoji: "✋",
        verb: "lève la main",
        priority: 1,
        severityColor: theme.colors.accentDark,
        notify: true,
    },
    [EVENT_TYPES.OUT_OF_APP]: {
        emoji: "👻",
        verb: "est sorti de l'appli",
        priority: 3,
        severityColor: theme.colors.warning,
        notify: false,
    },
    [EVENT_TYPES.SILENCE]: {
        emoji: "⏱️",
        verb: "n'a pas parlé depuis 10 min",
        priority: 1,
        severityColor: theme.colors.primaryLight,
        notify: true,
    },
    [EVENT_TYPES.EXERCISE_DONE]: {
        emoji: "✅",
        verb: "a soumis son exo",
        priority: 3,
        severityColor: theme.colors.success,
        notify: true,
    },
    [EVENT_TYPES.SAM_ONGOING]: {
        emoji: "🤖",
        verb: "discute avec Sam",
        priority: 5,
        severityColor: theme.colors.sam,
        notify: false,
    },
    [EVENT_TYPES.AUTO_ACTIVITY]: {
        emoji: "🧭",
        // Static fallback; the queue sets a richer per-event `verb` that also
        // includes the student's self-evaluation (see AUTO_EVAL_LABELS).
        verb: "a choisi sa prochaine activité",
        priority: 3,
        severityColor: theme.colors.primary,
        notify: true,
    },
}

export const eventHash = (event) =>
    `${event.type}-${event.studentId}-${event.startedAt}`

export const getEffectiveCta = (event, { dialogueWithStudentId, isWeb }) => {
    const firstname = event.student?.firstname ?? "?"

    if (event.type === EVENT_TYPES.SAM_ONGOING) {
        return {
            kind: "passive",
            label: `Voir résumé de ${firstname}`,
            action: "openAgentSummary",
        }
    }

    if (SAM_ELIGIBLE.has(event.type)) {
        if (event.samSent && !event.escalated) {
            return {
                kind: "passive",
                label: `Sam aide ${firstname}`,
                action: "openAgentSummary",
            }
        }

        if (event.escalated) {
            return {
                kind: "active",
                label: `Parler à ${firstname}`,
                action: "pinAndSetDialogue",
            }
        }

        const tutorBusyWithOther =
            dialogueWithStudentId != null &&
            dialogueWithStudentId !== event.studentId
        if (tutorBusyWithOther) {
            return {
                kind: "active",
                label: `Envoyer Sam à ${firstname}`,
                action: "sendSam",
            }
        }

        return {
            kind: "active",
            label: `Parler à ${firstname}`,
            action: "pinAndSetDialogue",
        }
    }

    if (event.type === EVENT_TYPES.OUT_OF_APP) {
        if (isWeb) {
            return {
                kind: "active",
                label: `Parler à ${firstname}`,
                action: "pinAndSetDialogue",
            }
        }
        return {
            kind: "active",
            label: `Notifier ${firstname}`,
            action: "notifyStudent",
        }
    }

    if (event.type === EVENT_TYPES.EXERCISE_DONE) {
        return {
            kind: "active",
            label: `Voir le rendu de ${firstname}`,
            action: "openStudentActivity",
        }
    }

    if (event.type === EVENT_TYPES.AUTO_ACTIVITY) {
        return {
            kind: "active",
            label: `Voir l'énoncé de ${firstname}`,
            action: "openStudentActivity",
        }
    }

    return { kind: "none", label: "", action: null }
}

// Unresolved events with an actionable CTA — drives the bell badge count and
// the drawer header pill.
export const countActiveEvents = (events) =>
    events.filter((e) => !e.isResolved && e.cta?.kind === "active").length

export const sortEventsByPriority = (events) =>
    [...events].sort((a, b) => {
        const pa = EVENT_CATALOG[a.type].priority
        const pb = EVENT_CATALOG[b.type].priority
        if (pa !== pb) return pa - pb
        return a.startedAt - b.startedAt
    })

export const formatElapsed = (startedAt, now) => {
    const sec = Math.max(0, Math.floor((now - startedAt) / 1000))
    if (sec < 60) return `${sec}s`
    const min = Math.floor(sec / 60)
    return `${min} min`
}

// Opacity for the dismiss/close controls: hover-reveal on web (clean desktop),
// but always partly visible on touch devices (tablet tutors) where onHoverIn
// never fires.
export const dismissControlOpacity = (hovered) => {
    if (hovered) return 0.85
    return IS_WEB ? 0 : 0.5
}
